<template>
    <component
        :is="to ? 'router-link' : href ? 'a' : 'button'"
        class="transition-[height,_padding,_font-size,_gap]"
        :class="[
            `ds-${to || href ? 'link' : 'button'}`,
            `ds-${variant}`,
            `ds-size-${btnSize}`,
            {
                destructive,
                'ds-loading': loading,
                'ds-on-dark-bg': onDarkBg,
                'ds-square': iconOnly,
            },
        ]"
        v-bind="to ? { to } : { href, target }"
        :disabled="!to && !href ? disabled || loading : undefined"
        :download
        :type="!to && !href ? type : undefined"
    >
        <slot name="leading">
            <DsIcon
                v-if="leading"
                :icon="leading"
                :size="iconSize"
            />
        </slot>

        <div class="relative">
            <slot>
                <!-- Button text will appear here -->
            </slot>
            <DsLoadingSpinner
                v-if="loading"
                class="loading-spinner"
                :size="iconSize"
            />
        </div>

        <slot name="trailing">
            <DsIcon
                v-if="trailing"
                :icon="trailing"
                :size="iconSize"
            />
        </slot>
    </component>
</template>

<script lang="ts" setup>
import type { BreakpointSize } from '@/components/design-system/design-system';
import { getLargestBreakpointMatch } from '@ds/design-system-breakpoints';
import DsIcon from '@ds/DsIcon.vue';
import DsLoadingSpinner from '@ds/spinner/DsSpinner.vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

type ButtonSize = 'lg' | 'md' | 'sm';

export interface DsButtonProps {
    destructive?: boolean;
    disabled?: boolean;
    href?: HTMLAnchorElement['href'] | null;
    to?: RouteLocationRaw;
    iconOnly?: boolean;
    leading?: FontAwesomeIconProps['icon'];
    loading?: boolean;
    onDarkBg?: boolean;
    rel?: HTMLAnchorElement['rel'];
    size: ButtonSize | Partial<Record<BreakpointSize, ButtonSize>>;
    target?: HTMLAnchorElement['target'];
    trailing?: FontAwesomeIconProps['icon'];
    type?: HTMLInputElement['type'];
    variant: 'primary' | 'secondary' | 'tertiary' | 'icon' | 'link-button';
    download?: string;
}

const props = withDefaults(defineProps<DsButtonProps>(), {
    type: 'button',
});

const btnSize = computed<ButtonSize>(() => {
    if (typeof props.size === 'string') return props.size;
    return getLargestBreakpointMatch(props.size) ?? 'md';
});
const iconSize = computed(
    () =>
        (
            ({
                lg: 'md',
                md: 'md',
                sm: 'sm',
            }) as const satisfies Record<ButtonSize, string>
        )[btnSize.value],
);
</script>

<style scoped lang="scss">
.ds-link {
    text-decoration: none;
}

.ds-link,
.ds-button {
    @apply inline-flex min-w-max cursor-pointer items-center justify-center rounded-lg border;
    transition: background-color 200ms;

    &:focus-visible {
        @apply outline-none ring-4 ring-ds-gray-300 ring-offset-ds-gray-200;
    }
}

.ds-primary {
    @apply border-none bg-semantic-primary-default text-content-inverse;

    &:hover {
        @apply bg-semantic-primary-hover;
    }

    &:active {
        @apply bg-semantic-primary-pressed;
    }

    &:disabled {
        @apply cursor-not-allowed bg-ds-gray-200;

        &:not(.ds-loading) {
            @apply border-ds-gray-200 text-content-disabled;
        }
    }

    &.ds-on-dark-bg {
        @apply bg-semantic-inverted-default text-content-primary;

        &:hover {
            @apply bg-semantic-inverted-hover;
        }

        &:focus-visible {
            @apply bg-semantic-primary-default text-white;
        }

        &:active {
            @apply bg-semantic-inverted-pressed text-content-primary;
        }

        &:disabled {
            @apply bg-ds-gray-300 text-content-disabled;
        }

        &.ds-loading {
            @apply border-none bg-semantic-primary-pressed text-semantic-primary-pressed;

            .loading-spinner {
                @apply text-content-inverse;
            }
        }
    }

    &.destructive {
        @apply border-none bg-semantic-destructive-primary-default;

        &:hover {
            @apply bg-semantic-destructive-primary-hover;
        }

        &:active {
            @apply bg-semantic-destructive-primary-pressed;
        }

        &:disabled {
            @apply bg-ds-red-200;

            &:not(.ds-loading) {
                @apply border-ds-red-200 text-content-disabled;
            }
        }

        &.ds-loading {
            @apply bg-semantic-destructive-primary-pressed;
        }
    }

    &.ds-loading {
        @apply cursor-progress border-semantic-primary-pressed bg-semantic-primary-pressed text-transparent;

        .loading-spinner {
            @apply text-content-inverse;
        }
    }
}

.ds-secondary,
.ds-tertiary {
    @apply border-ds-gray-500 bg-semantic-secondary-default text-content-primary;

    &:hover {
        @apply bg-semantic-secondary-hover;
    }

    &:focus-visible {
        @apply border-ds-gray-500;
    }

    &:active {
        @apply bg-semantic-secondary-pressed;
    }

    &:disabled {
        @apply cursor-not-allowed border-ds-gray-300 bg-white;

        &:not(.ds-loading) {
            @apply text-content-disabled;
        }
    }

    &.ds-on-dark-bg {
        @apply text-content-inverse;

        &:not(.ds-tertiary) {
            // tertiary has no background
            @apply border-semantic-inverted-default bg-semantic-primary-default;
        }

        &:hover:not(:disabled) {
            @apply bg-semantic-onDark-hover;
        }

        &:focus-visible {
            @apply bg-semantic-onDark-hover;
        }

        &:active:not(:disabled) {
            @apply bg-semantic-onDark-pressed;
        }

        &:disabled {
            @apply border-ds-gray-500;

            &:not(.ds-loading) {
                @apply text-ds-gray-500;
            }
        }

        &.ds-loading {
            @apply border-none bg-semantic-onDark-pressed;

            .loading-spinner {
                @apply text-content-inverse;
            }
        }
    }

    &.destructive {
        @apply border-semantic-destructive-primary-default text-semantic-destructive-primary-default;

        &:hover:not(:disabled) {
            @apply bg-semantic-destructive-secondary-hover;
        }

        &:active:not(:disabled) {
            @apply bg-semantic-destructive-secondary-pressed;
        }

        &:disabled {
            &:not(.ds-loading) {
                @apply border-ds-red-400 text-ds-red-400;
            }
        }

        &.ds-loading {
            @apply border-semantic-destructive-secondary-pressed bg-semantic-destructive-secondary-pressed;

            .loading-spinner {
                @apply text-ds-red-500;
            }
        }
    }

    &.ds-loading {
        @apply cursor-progress border-ds-gray-500 bg-semantic-secondary-pressed text-transparent;

        .loading-spinner {
            @apply text-content-primary;
        }
    }
}

.ds-tertiary {
    @apply border-none underline;

    &:focus-visible {
        @apply border-none;
    }

    &.ds-loading {
        @apply border-none;
    }

    &:disabled {
        @apply border-none;
    }

    &.ds-on-dark-bg {
        @apply border-none;

        &:disabled {
            @apply border-none;
        }
    }

    &.destructive {
        @apply border-none;

        &:disabled {
            @apply border-none;
        }
    }
}

.ds-link-button {
    @apply border-transparent bg-transparent text-semantic-primary-default underline;

    &.ds-size-lg,
    &.ds-size-md,
    &.ds-size-sm {
        @apply h-auto px-0;
    }

    &:hover {
        @apply text-semantic-primary-hover;
    }

    &:active {
        @apply text-semantic-primary-pressed;
    }

    &:disabled {
        @apply cursor-not-allowed text-content-disabled;
    }

    &.on-dark-bg {
        @apply text-semantic-inverted-default;

        &:hover {
            @apply text-semantic-inverted-hover;
        }

        &:active {
            @apply text-semantic-inverted-pressed;
        }

        &:disabled {
            @apply text-content-disabled;
        }
    }

    &.destructive {
        @apply text-semantic-destructive-primary-default;

        &:hover {
            @apply text-semantic-destructive-primary-hover;
        }
    }

    &.ds-loading {
        @apply text-transparent;

        .loading-spinner {
            @apply text-semantic-primary-default;
        }
    }
}

.ds-icon {
    @apply border-transparent bg-ds-gray-800 bg-opacity-0 text-ds-gray-700;

    &:hover {
        @apply bg-opacity-5;
    }

    &:active {
        @apply bg-opacity-10;
    }

    &:disabled {
        @apply cursor-not-allowed bg-opacity-20;

        &:not(.ds-loading) {
            @apply text-content-disabled;
        }
    }

    &.ds-on-dark-bg {
        @apply bg-ds-gray-200 bg-opacity-0 text-white;

        &:hover {
            @apply bg-ds-gray-100 bg-opacity-20;
        }

        &:active {
            @apply bg-opacity-35;
        }

        &:disabled {
            @apply bg-opacity-40;

            &:not(.ds-loading) {
                @apply text-content-disabled;
            }
        }

        &.ds-loading {
            @apply bg-opacity-35;

            .loading-spinner {
                @apply text-white;
            }
        }
    }

    &.destructive {
        @apply border-transparent bg-ds-red-800 bg-opacity-0 text-ds-red-600;

        &:hover {
            @apply bg-opacity-5;
        }

        &:active {
            @apply bg-opacity-10;
        }

        &:disabled {
            &:not(.ds-loading) {
                @apply text-ds-red-300;
            }
        }

        &.ds-loading {
            @apply bg-opacity-10;

            .loading-spinner {
                @apply text-ds-red-700;
            }
        }
    }

    &.ds-loading {
        @apply cursor-progress bg-ds-gray-200 text-transparent;

        .loading-spinner {
            @apply bg-opacity-10;
        }
    }
}

.ds-size-lg {
    @apply h-12 gap-2 px-6 text-base;
}

.ds-size-md {
    @apply h-10 gap-2 px-4 text-base;
}

.ds-size-sm {
    @apply h-8 gap-1 px-2 text-sm;
}

.ds-square {
    @apply aspect-square items-center justify-center p-0;
}

.loading-spinner {
    @apply absolute inset-0 flex items-center justify-center;
}
</style>
